import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AutocompleteChangeReason, AutocompleteInputChangeReason, Tooltip } from '@mui/material';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { AgencyCriteria } from '../types/Criteria/AgencyCriteria';
import { Agency } from '../types/Agency';
import { InfiniteAutocompleteProps } from './InfiniteAutocomplete';
import { AxiosInfinitePageableResponse, PageableRequest, PageableResponse } from '../types/AxiosPageableResponse';
import { PAGEABLE_SIZE } from '../types/AxiosPageableResponse';
import InfiniteAutocomplete from './InfiniteAutocomplete';
import Utils from './Utils';

/**
 * Agency Autocomplete Utilities
 */

const getOptionKey = (option: Agency | any): string | number => {
    return option?.id as number;
};

const getOptionLabel = (option: Agency | any): string => {
    return option?.name || '';
};

/**
 * Agency Autocomplete Requests
 */

const defaultPageable: PageableRequest = {
    page: 1,
    size: PAGEABLE_SIZE,
    sort: 'asc',
    sortBy: 'name',
};

const fetch = async (
    criteria: AgencyCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<PageableResponse<Agency>> => {
    const source = axios.CancelToken.source();

    if (signal) {
        if (signal.aborted) source.cancel('aborted');
        signal.addEventListener('abort', () => source.cancel('aborted'), { once: true });
    }

    const response = await axios.get('/api/agency/all', {
        headers: { XBusy: 'false' },
        params: {
            ...criteria,
            ...pageable,
            page: page - 1,
            ids: criteria?.ids?.join(',') || undefined,
            advertiserIds: criteria?.advertiserIds?.join(',') || undefined,
            oemIds: criteria?.oemIds?.join(',') || undefined,
            oemProgramIds: criteria?.oemProgramIds?.join(',') || undefined,
            agencyMarketRegions: criteria?.agencyMarketRegions?.join(',') || undefined,
        },
        cancelToken: source.token,
    });
    return response.data;
};

const fetchInitial = async (
    ids: number[] | number | null | undefined,
    criteria: AgencyCriteria,
    signal?: AbortSignal
): Promise<any> => {
    const _ids: number[] = Array.isArray(ids) ? ids : ids != null ? [ids] : [];

    if (_ids.length === 0) {
        return [];
    }

    const { content } = await fetch({ ...criteria, ids: _ids }, { page: 1, size: 1000 }, signal);

    return content;
};

const fetchInfinite = async (
    criteria: AgencyCriteria,
    { page = 1, ...pageable }: PageableRequest,
    signal?: AbortSignal
): Promise<AxiosInfinitePageableResponse<Agency>> => {
    const { content } = await fetch(criteria, { page, ...pageable }, signal);

    return {
        items: content,
        nextPage: content.length === pageable.size ? page + 1 : undefined,
    };
};

const fetchMass = async (criteria: AgencyCriteria, signal?: AbortSignal): Promise<any> => {
    const { content } = await fetch(criteria, { page: 1, size: 1000 }, signal);

    return content;
};

/**
 * Agency Autocomplete
 */

interface AgencyAutocompleteProps<
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
> extends Omit<
        InfiniteAutocompleteProps<Agency, Multiple, DisableClearable, FreeSolo>,
        'options' | 'value' | 'getOptionKey' | 'getOptionLabel'
    > {
    onLoading?: () => void;
    criteria?: AgencyCriteria;
    value: Multiple extends true ? number[] : number | null;
    deferChangeUntilClose?: boolean;
}

const AgencyAutocomplete = <
    Multiple extends boolean | undefined = undefined,
    DisableClearable extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
>({
    onLoading = () => {},
    onChange,
    disabled = false,
    deferChangeUntilClose = false,
    ...props
}: AgencyAutocompleteProps<Multiple, DisableClearable, FreeSolo>) => {
    const { debounce } = Utils;

    const [options, setOptions] = useState<Agency[]>([]);
    const [value, setValue] = useState<
        InfiniteAutocompleteProps<Agency, Multiple, DisableClearable, FreeSolo>['value']
    >((props.multiple ? [] : null) as InfiniteAutocompleteProps<Agency, Multiple, DisableClearable, FreeSolo>['value']);
    const [inputValue, setInputValue] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isMassSelected, setIsMassSelected] = useState<boolean>(false);
    const pendingValueRef = useRef<
        InfiniteAutocompleteProps<Agency, Multiple, DisableClearable, FreeSolo>['value'] | null
    >(null);
    const hasChangedWhileOpenRef = useRef<boolean>(false);

    const criteriaWithoutName = useMemo((): AgencyCriteria => {
        return {
            ...props?.criteria,
        };
    }, [props.criteria]);

    const criteria = useMemo((): AgencyCriteria => {
        return {
            ...criteriaWithoutName,
            name: inputValue,
        };
    }, [criteriaWithoutName, inputValue]);

    // Fetch initial value if IDs are provided
    const { data: initialValue, isLoading: isLoadingInitialValue } = useQuery({
        queryKey: ['infinite/agencies/initial', props.value, criteriaWithoutName],
        queryFn: ({ signal }) => fetchInitial(props.value, criteria, signal),
        enabled: true,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (initialValue && initialValue.length > 0) {
            if (props.multiple) {
                setValue(initialValue);
            } else {
                setValue(initialValue[0]);
            }
        }
    }, [initialValue, props.multiple]);

    const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, refetch } = useInfiniteQuery({
        queryKey: ['infinite/agencies', criteria],
        queryFn: ({ pageParam, signal }) =>
            fetchInfinite(criteria, { page: pageParam || 1, size: defaultPageable.size }, signal),
        getNextPageParam: (lastPage) => lastPage.nextPage,
        initialPageParam: 0,
        // staleTime: 1000 * 60 * 5,
        enabled: true,
        refetchOnWindowFocus: false,
    });

    const { refetch: refetchMass, isLoading: isLoadingMass } = useQuery({
        queryKey: ['infinite/agencies/mass', criteria],
        queryFn: ({ signal }) => fetchMass(criteria, signal),
        enabled: false,
        refetchOnWindowFocus: false,
    });

    const handleOnClose = useCallback((): void => {
        setIsOpen(false);
        if (deferChangeUntilClose && hasChangedWhileOpenRef.current && pendingValueRef.current !== null) {
            onChange(pendingValueRef.current as any);
        }
        hasChangedWhileOpenRef.current = false;
        pendingValueRef.current = null;
    }, [deferChangeUntilClose, onChange]);

    const handleOnChange = useCallback(
        (
            newValue: InfiniteAutocompleteProps<Agency, Multiple, DisableClearable, FreeSolo>['value'] | undefined,
            reason?: AutocompleteChangeReason
        ): void => {
            setValue(newValue);
            if (deferChangeUntilClose) {
                pendingValueRef.current = newValue ?? null;
                hasChangedWhileOpenRef.current = true;
            } else {
                onChange(newValue as any);
            }

            // If cleared via X button, trigger close logic immediately
            if (reason === 'clear') {
                handleOnClose();
            }
        },
        [deferChangeUntilClose, handleOnClose, onChange]
    );

    const debouncedOnInputChange = useRef(
        debounce((newInputValue: string): void => {
            refetch();
        }, 1000)
    ).current;

    const isLoading = useMemo((): boolean => {
        return isFetching || isFetchingNextPage || isLoadingInitialValue || isLoadingMass;
    }, [isFetching, isFetchingNextPage, isLoadingInitialValue, isLoadingMass]);

    const isSearching = inputValue.trim().length > 0;
    const isDisabled = disabled || (!isLoading && options.length === 0 && !isSearching);

    const handleOnInputChange = (
        event: React.SyntheticEvent,
        newInputValue: string,
        reason: AutocompleteInputChangeReason
    ): void => {
        setInputValue(newInputValue);
        debouncedOnInputChange(newInputValue);
    };

    const handleOnScroll = useCallback((): void => {
        if (!isMassSelected) {
            if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
            }
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage, isMassSelected]);

    useEffect((): void => {
        setOptions(data?.pages.flatMap((page) => page.items) || []);
    }, [data]);

    useEffect((): void => {
        if (isLoading === true) {
            onLoading();
        }
    }, [isLoading, onLoading]);

    useEffect(() => {
        if (isOpen === false) {
            setInputValue('');
        }
    }, [isOpen]);

    useEffect(() => {
        setIsMassSelected(false);
    }, [criteria]);

    const handleOnFetchMass = async (): Promise<ReadonlyArray<Agency>> => {
        const { data } = await refetchMass();

        setIsMassSelected(true);

        return data;
    };

    return (
        <Tooltip
            arrow
            disableHoverListener={!isDisabled}
            title="The agency filter is disabled because there are no agencies matching the current filter selections."
        >
            <InfiniteAutocomplete
                disableCloseOnSelect={props.multiple}
                openOnFocus={props.multiple}
                selectOnFocus={props.multiple}
                blurOnSelect={false}
                clearOnBlur={false}
                label="Agency"
                {...props}
                disabled={isDisabled}
                getOptionKey={getOptionKey}
                getOptionLabel={getOptionLabel}
                options={options}
                value={value}
                loading={isLoading}
                onChange={handleOnChange}
                inputValue={inputValue}
                onInputChange={handleOnInputChange}
                onScroll={handleOnScroll}
                onFetchMass={handleOnFetchMass}
                onOpen={(): void => setIsOpen(true)}
                onClose={handleOnClose}
            />
        </Tooltip>
    );
};

export type { AgencyAutocompleteProps };
export { defaultPageable as defaultAgencyPageable };
export default AgencyAutocomplete;
